import { useState } from "react";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, Copy, KeyRound, Smartphone } from "lucide-react";
import { Txt } from "@/components/Txt";
import { passkeysSupported, runRegistration, runAuthentication } from "@/lib/passkey";

export default function TwoFactor({ data, onComplete }) {
  const isEnroll = data.mode === "enroll";
  const methods = data.methods || {};
  const canPasskey = passkeysSupported();

  const [view, setView] = useState(isEnroll ? "choose" : "verify");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [backup, setBackup] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [showCode, setShowCode] = useState(!!methods.totp);

  const fail = (e, fallback) => {
    if (e?.name === "NotAllowedError") setError("The passkey prompt was cancelled or timed out. Please try again.");
    else setError(e?.response?.data?.detail || fallback);
  };

  const submitTotpEnroll = async () => {
    setError(""); setBusy(true);
    try {
      const r = await api.post("/auth/2fa/enroll/verify", { twofa_token: data.twofa_token, code });
      if (r.data.backup_codes) setBackup(r.data.backup_codes);
      else onComplete();
    } catch (e) { fail(e, "That code didn't match. Check your authenticator app and try again."); }
    finally { setBusy(false); }
  };

  const submitCodeVerify = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { twofa_token: data.twofa_token, code });
      onComplete();
    } catch (e) { fail(e, "That code didn't match. Try your authenticator app or a backup code."); }
    finally { setBusy(false); }
  };

  const enrollPasskey = async () => {
    setError(""); setBusy(true);
    try {
      const opts = await api.post("/auth/2fa/passkey/register/options", { twofa_token: data.twofa_token });
      const cred = await runRegistration(opts.data);
      const r = await api.post("/auth/2fa/passkey/register/verify", { twofa_token: data.twofa_token, credential: cred, label: "Passkey" });
      if (r.data.backup_codes) setBackup(r.data.backup_codes);
      else onComplete();
    } catch (e) { fail(e, "Couldn't register that passkey. Please try again."); }
    finally { setBusy(false); }
  };

  const verifyPasskey = async () => {
    setError(""); setBusy(true);
    try {
      const opts = await api.post("/auth/2fa/passkey/auth/options", { twofa_token: data.twofa_token });
      const cred = await runAuthentication(opts.data);
      await api.post("/auth/2fa/passkey/auth/verify", { twofa_token: data.twofa_token, credential: cred });
      onComplete();
    } catch (e) { fail(e, "That passkey didn't verify. Please try again."); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-warm-cream border border-ink/20 rounded-xl px-4 py-3 min-h-[50px] text-[18px] tracking-widest text-center focus:bg-white";
  const errorBox = error && <div data-testid="twofa-error" className="bg-maroon/10 border border-maroon/30 text-ink rounded-lg px-4 py-3 mb-3 text-[15px]">{error}</div>;

  if (backup) {
    return (
      <div data-testid="backup-codes-screen">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-6 h-6 text-olive" /><h2 className="font-zilla text-2xl font-semibold"><Txt id="twofa.backup.title" screen="Two-factor">Save your backup codes</Txt></h2></div>
        <p className="text-ink/70 text-[15px] mb-4"><Txt id="twofa.backup.subtitle" screen="Two-factor">Each code works once if you ever lose your authenticator. Store them somewhere safe — this is the only time they're shown.</Txt></p>
        <div className="grid grid-cols-2 gap-2 bg-warm-cream border border-ink/15 rounded-lg p-4 mb-4 font-mono text-[15px]" data-testid="backup-codes-list">
          {backup.map((c) => <div key={c}>{c}</div>)}
        </div>
        <button data-testid="copy-backup-codes" onClick={() => { navigator.clipboard?.writeText(backup.join("\n")); }} className="flex items-center gap-1.5 text-[14px] text-dark-olive font-semibold mb-4"><Copy className="w-4 h-4" /> Copy all</button>
        <button data-testid="backup-continue" onClick={onComplete} className="w-full bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg"><Txt id="twofa.backup.continue" screen="Two-factor">I've saved them — continue</Txt></button>
      </div>
    );
  }

  // ENROLL — choose a method
  if (isEnroll && view === "choose") {
    return (
      <div data-testid="twofactor-screen">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-6 h-6 text-maroon" /><h2 className="font-zilla text-2xl font-semibold"><Txt id="twofa.choose.title" screen="Two-factor">Set up two-factor</Txt></h2></div>
        <p className="text-ink/70 text-[15px] mb-4"><Txt id="twofa.choose.subtitle" screen="Two-factor">Your role has access to sensitive data, so a second step is required. Choose how you'd like to verify.</Txt></p>
        {errorBox}
        <div className="space-y-3" data-testid="twofa-choose">
          {canPasskey && (
            <button data-testid="choose-passkey" disabled={busy} onClick={enrollPasskey} className="w-full flex items-start gap-3 text-left border-[1.5px] border-ink/25 rounded-xl px-4 py-4 hover:bg-ink/5 disabled:opacity-50">
              <KeyRound className="w-6 h-6 text-maroon shrink-0 mt-0.5" />
              <span>
                <span className="block font-semibold text-[16px]">{busy ? "Waiting for your device…" : <Txt id="twofa.choose.passkey" screen="Two-factor">Use a passkey</Txt>}</span>
                <span className="block text-[14px] text-ink/60"><Txt id="twofa.choose.passkey_desc" screen="Two-factor">Face ID, Touch ID, Windows Hello, your phone, or a security key.</Txt></span>
              </span>
            </button>
          )}
          <button data-testid="choose-totp" disabled={busy} onClick={() => { setError(""); setView("totp"); }} className="w-full flex items-start gap-3 text-left border-[1.5px] border-ink/25 rounded-xl px-4 py-4 hover:bg-ink/5 disabled:opacity-50">
            <Smartphone className="w-6 h-6 text-dark-olive shrink-0 mt-0.5" />
            <span>
              <span className="block font-semibold text-[16px]"><Txt id="twofa.choose.totp" screen="Two-factor">Use an authenticator app</Txt></span>
              <span className="block text-[14px] text-ink/60"><Txt id="twofa.choose.totp_desc" screen="Two-factor">Google Authenticator, Authy, 1Password, or similar.</Txt></span>
            </span>
          </button>
        </div>
      </div>
    );
  }

  // ENROLL — authenticator app (TOTP)
  if (isEnroll && view === "totp") {
    return (
      <div data-testid="twofactor-screen">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-6 h-6 text-maroon" /><h2 className="font-zilla text-2xl font-semibold"><Txt id="twofa.enroll.title" screen="Two-factor">Set up two-factor</Txt></h2></div>
        <p className="text-ink/70 text-[15px] mb-3"><Txt id="twofa.enroll.instructions" screen="Two-factor">Your role has access to sensitive data, so a second step is required. Scan this with Google Authenticator, Authy, or any authenticator app, then enter the 6-digit code.</Txt></p>
        {data.qr && <img src={data.qr} alt="Authenticator QR code" data-testid="twofa-qr" className="w-44 h-44 mx-auto border border-ink/15 rounded-lg bg-white p-2 mb-3" />}
        {data.otpauth_url && (
          <div className="mb-3">
            <button type="button" data-testid="twofa-manual-toggle" onClick={() => setShowManual((v) => !v)} className="text-[13px] text-dark-olive font-semibold hover:underline">
              {showManual ? "Hide manual code" : "Can't scan? Enter code manually"}
            </button>
            {showManual && <p className="text-[13px] font-mono break-all mt-1 bg-warm-cream border border-ink/15 rounded px-3 py-2" data-testid="twofa-otpauth">{(data.otpauth_url.match(/secret=([A-Z2-7]+)/) || [])[1]}</p>}
          </div>
        )}
        {errorBox}
        <input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" autoFocus onKeyDown={(e) => e.key === "Enter" && submitTotpEnroll()} className={inputCls + " mb-3"} />
        <button data-testid="twofa-submit" onClick={submitTotpEnroll} disabled={busy || !code.trim()} className="w-full flex items-center justify-center gap-2 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg disabled:opacity-50">
          {busy && <Loader2 className="w-5 h-5 animate-spin" />} <Txt id="twofa.enroll.submit" screen="Two-factor">Verify & finish</Txt>
        </button>
        {canPasskey && <button data-testid="back-to-choose" onClick={() => { setError(""); setView("choose"); }} className="w-full mt-3 text-[14px] text-ink/60 hover:underline">Back to options</button>}
      </div>
    );
  }

  // VERIFY
  return (
    <div data-testid="twofactor-screen">
      <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-6 h-6 text-maroon" /><h2 className="font-zilla text-2xl font-semibold"><Txt id="twofa.verify.title" screen="Two-factor">Two-factor authentication</Txt></h2></div>
      <p className="text-ink/70 text-[15px] mb-3"><Txt id="twofa.verify.instructions" screen="Two-factor">Enter the 6-digit code from your authenticator app, or one of your backup codes.</Txt></p>
      {errorBox}
      {methods.passkey && (
        <button data-testid="verify-passkey-btn" onClick={verifyPasskey} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg disabled:opacity-50 mb-3">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />} <Txt id="twofa.verify.passkey_btn" screen="Two-factor">Verify with your passkey</Txt>
        </button>
      )}
      {methods.passkey && !showCode && (
        <button data-testid="show-code-toggle" onClick={() => setShowCode(true)} className="w-full text-[14px] text-dark-olive font-semibold hover:underline mb-1"><Txt id="twofa.verify.use_code" screen="Two-factor">Use an authenticator or backup code instead</Txt></button>
      )}
      {showCode && (
        <>
          <input data-testid="twofa-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code or backup code" autoFocus onKeyDown={(e) => e.key === "Enter" && submitCodeVerify()} className={inputCls + " mb-3 mt-1"} />
          <button data-testid="twofa-submit" onClick={submitCodeVerify} disabled={busy || !code.trim()} className={`w-full flex items-center justify-center gap-2 font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg disabled:opacity-50 ${methods.passkey ? "border-[1.5px] border-ink/25" : "bg-maroon text-cream"}`}>
            {busy && <Loader2 className="w-5 h-5 animate-spin" />} <Txt id="twofa.verify.submit" screen="Two-factor">Verify</Txt>
          </button>
        </>
      )}
    </div>
  );
}
